

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewPaymentTermModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        payment_due: 'Days Prior to Departure',
        days: '',
        amount_percent: '',
        amount_actual: '',
        payment_type: '',
        refund_term: '',
        applicable_for_id: '',
        category: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [accounts, setAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchAccounts();
            // Reset form when modal opens
            setFormData({
                name: '',
                payment_due: 'Days Prior to Departure',
                days: '',
                amount_percent: '',
                amount_actual: '',
                payment_type: '',
                refund_term: '',
                applicable_for_id: '',
                category: ''
            });
            setFormErrors({});
        }
    }, [isOpen]);

    const fetchAccounts = async () => {
        setLoadingAccounts(true);
        try {
            const response = await axiosInstance.get('/accounts/?dropdown=true');
            setAccounts(response.data || []);
        } catch (error) {
            console.error('Error fetching accounts:', error);
        } finally {
            setLoadingAccounts(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = {
                ...prev,
                [name]: value
            };
            // Auto-generate name if payment_type or payment_due changes, but only if name is empty or was auto-generated
            if ((name === 'payment_type' || name === 'payment_due' || name === 'days' || name === 'amount_percent' || name === 'amount_actual') && (!prev.name || prev.name === generatePaymentTermName(prev))) {
                updated.name = generatePaymentTermName(updated);
            }
            return updated;
        });
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const generatePaymentTermName = (data) => {
        if (!data.payment_type && !data.payment_due) return '';
        const parts = [];
        if (data.payment_type) parts.push(data.payment_type);
        if (data.payment_due) parts.push(data.payment_due);
        if (data.days) parts.push(`${data.days} days`);
        if (data.amount_percent) parts.push(`${data.amount_percent}%`);
        if (data.amount_actual) parts.push(`$${data.amount_actual}`);
        return parts.join(' - ') || '';
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.payment_due) errors.payment_due = 'Payment Due is required';
        if (!formData.payment_type) errors.payment_type = 'Payment Type is required';
        if (!formData.name || formData.name.trim() === '') {
            // Auto-generate name if not provided
            const generatedName = generatePaymentTermName(formData);
            if (generatedName) {
                setFormData(prev => ({ ...prev, name: generatedName }));
            } else {
                errors.name = 'Name is required';
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            // Ensure name is set
            const finalName = formData.name.trim() || generatePaymentTermName(formData);
            
            const payload = {
                ...formData,
                name: finalName,
                days: formData.days ? parseInt(formData.days) : null,
                amount_percent: formData.amount_percent ? parseFloat(formData.amount_percent) : null,
                amount_actual: formData.amount_actual ? parseFloat(formData.amount_actual) : null,
                applicable_for_id: formData.applicable_for_id ? parseInt(formData.applicable_for_id) : null,
                refund_term: formData.refund_term || null,
                category: formData.category || null,
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            setFormData({
                name: '',
                payment_due: 'Days Prior to Departure',
                days: '',
                amount_percent: '',
                amount_actual: '',
                payment_type: '',
                refund_term: '',
                applicable_for_id: '',
                category: ''
            });
            onClose();
        } catch (error) {
            console.error('Error saving Payment Term:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || error.message || 'Error saving Payment Term',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '80%', md: '700px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Payment Term</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                        error={!!formErrors.name}
                                        helperText={formErrors.name || 'Auto-generated from payment details (can be edited)'}
                                        required
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.payment_due} required>
                                        <InputLabel>Payment Due</InputLabel>
                                        <Select
                                            name="payment_due"
                                            value={formData.payment_due}
                                            onChange={handleInputChange}
                                            label="Payment Due"

                                        >
                                            <MenuItem value="On Booking Confirmation">On Booking Confirmation</MenuItem>
                                            <MenuItem value="Date of Departure">Date of Departure</MenuItem>
                                            <MenuItem value="Days Prior to Departure">Days Prior to Departure</MenuItem>
                                            <MenuItem value="Days after purchase">Days after purchase</MenuItem>
                                            <MenuItem value="Days after departure">Days after departure</MenuItem>
                                        </Select>
                                        {formErrors.payment_due && <MDTypography variant="caption" color="error">{formErrors.payment_due}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.payment_type} required>
                                        <InputLabel>Payment Type</InputLabel>
                                        <Select
                                            name="payment_type"
                                            value={formData.payment_type}
                                            onChange={handleInputChange}
                                            label="Payment Type"

                                        >
                                            <MenuItem value="Deposit">Deposit</MenuItem>
                                            <MenuItem value="Balance">Balance</MenuItem>
                                            <MenuItem value="Second Payment">Second Payment</MenuItem>
                                            <MenuItem value="Third Payment">Third Payment</MenuItem>
                                            <MenuItem value="Fourth Payment">Fourth Payment</MenuItem>
                                        </Select>
                                        {formErrors.payment_type && <MDTypography variant="caption" color="error">{formErrors.payment_type}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Days"
                                        name="days"
                                        type="number"
                                        value={formData.days}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Amount (%)"
                                        name="amount_percent"
                                        type="number"
                                        value={formData.amount_percent}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Amount ($)"
                                        name="amount_actual"
                                        type="number"
                                        value={formData.amount_actual}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Refund Term</InputLabel>
                                        <Select
                                            name="refund_term"
                                            value={formData.refund_term}
                                            onChange={handleInputChange}
                                            label="Refund Term"

                                        >
                                            <MenuItem value=""><em>None</em></MenuItem>
                                            <MenuItem value="Per Pax">Per Pax</MenuItem>
                                            <MenuItem value="Per Qty">Per Qty</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Category</InputLabel>
                                        <Select
                                            name="category"
                                            value={formData.category}
                                            onChange={handleInputChange}
                                            label="Category"

                                        >
                                            <MenuItem value=""><em>None</em></MenuItem>
                                            <MenuItem value="Merchant">Merchant</MenuItem>
                                            <MenuItem value="Guide">Guide</MenuItem>
                                            <MenuItem value="Influencer">Influencer</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Applicable For</InputLabel>
                                        <Select
                                            name="applicable_for_id"
                                            value={formData.applicable_for_id}
                                            onChange={handleInputChange}
                                            label="Applicable For"

                                            disabled={loadingAccounts}
                                        >
                                            <MenuItem value=""><em>Select an Account</em></MenuItem>
                                            {accounts.map(acc => (
                                                <MenuItem key={acc.id} value={acc.id}>{acc.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Payment Term'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewPaymentTermModal;

